import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, BookOpen, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);
        } else if (data.status === 'complete' && data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);
        } else if (data.status === 'expired') {
          setStatus('error');
        } else if (attempts < 10) {
          // Keep polling - up to 10 attempts with 3s intervals
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 3000);
        } else {
          // Max attempts reached - may still be processing
          setStatus('error');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts < 10) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 3000);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, attempts]);

  if (status === 'loading') {
    return (
      <div data-testid="payment-loading" className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center p-8">
          <Loader2 className="w-12 h-12 text-sage animate-spin mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-charcoal mb-2">Processing your payment...</h2>
          <p className="text-charcoal-light">Please wait while we confirm your purchase</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div data-testid="payment-error" className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center p-8 max-w-md">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="font-heading text-2xl text-charcoal mb-2">Something went wrong</h2>
          <p className="text-charcoal-light mb-6">
            We couldn't confirm your payment. If you were charged, please contact support.
          </p>
          <Link
            to="/purchase"
            className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all"
          >
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="payment-success" className="min-h-screen bg-cream py-12 px-6">
      <div className="max-w-2xl mx-auto text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-fade-in">
          <CheckCircle className="w-10 h-10 text-sage" />
        </div>

        <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-4 animate-fade-in">
          Welcome to Your Breakthrough!
        </h1>
        <p className="text-lg text-charcoal-light mb-4">
          Your purchase was successful. You now have full access to Breakthrough.
        </p>
        <p className="text-base text-sage font-medium mb-8">
          You will instantly receive your Purpose Reclaim ebook download and bonus resources below.
        </p>

        {/* Order Summary */}
        {paymentData && (
          <div className="bg-white rounded-xl p-6 shadow-sm mb-8 text-left">
            <h3 className="font-medium text-charcoal mb-4">Order Summary</h3>
            <div className="flex justify-between items-center pb-4 border-b border-stone-100">
              <span className="text-charcoal-light">
                {paymentData.metadata?.product_name || 'Breakthrough E-Book'}
              </span>
              <span className="font-medium text-charcoal">
                £{paymentData.amount?.toFixed(2) || '29.00'}
              </span>
            </div>
            <div className="flex justify-between items-center pt-4">
              <span className="font-medium text-charcoal">Total Paid</span>
              <span className="font-heading text-xl text-sage">
                £{paymentData.amount?.toFixed(2) || '29.00'}
              </span>
            </div>
          </div>
        )}

        {/* Access Buttons */}
        <div className="space-y-4">
          <Link
            to="/contents"
            data-testid="start-reading-btn"
            className="inline-flex items-center justify-center gap-2 w-full max-w-sm px-8 py-4 bg-sage text-white rounded-full font-medium hover:bg-sage-dark transition-all shadow-lg"
          >
            <BookOpen className="w-5 h-5" />
            Start Reading Now
          </Link>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/downloads"
              data-testid="downloads-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold text-white rounded-full font-medium hover:bg-gold/90 transition-all"
            >
              Download PDF
            </Link>
            <Link
              to="/journal"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-sage border border-sage/20 rounded-full font-medium hover:bg-sage/5 transition-all"
            >
              My Journal
            </Link>
          </div>
        </div>

        {/* Encouragement */}
        <div className="mt-12 p-6 bg-gradient-to-r from-sky-light/30 to-sage-light/20 rounded-xl">
          <p className="font-heading text-xl italic text-charcoal mb-2">
            "He heals the brokenhearted and binds up their wounds."
          </p>
          <p className="text-sm text-gold">— Psalm 147:3</p>
        </div>

        <p className="text-sm text-charcoal-light mt-8">
          A confirmation email will be sent to your address. If you have any questions, 
          please don't hesitate to reach out.
        </p>
      </div>
    </div>
  );
};
